import clsx from 'clsx';
import ProjectModeTag from 'components/feature/project/projectModeTag/ProjectModeTag';
import { InfoItem } from 'components/molecules/InfoItem/InfoItem';
import { emptyDisplayValue } from 'constants/common';
import {
  AssessmentStageFragmentFragment,
  AssessmentStageKind,
  CognitiveMode,
  ProjectStatus,
} from 'generated/graphql';
import { TCognitiveSkillsVersion } from 'helpers/cognitiveSkills';
import { stageKindLabel } from 'views/Project/components/Stages/helpers';
import {
  InlineNotification,
  NotificationType,
} from '@spotted-zebra-uk/ui-components';
import CandidateListButton from '../StageActionButtons/DirectInvitation';
import IndirectInvitationButton from '../StageActionButtons/IndirectInvitationButton';
import {
  getExpiryOptionLabel,
  getStageDate,
  getYesOrNo,
  parseActorRolesToString,
} from './StageOverview.helpers';
import styles from './StageOverview.module.scss';

interface IStageOverview {
  stage: AssessmentStageFragmentFragment;
  projectSubId: string;
  projectId: number;
  hasCompanyExpiryOption: boolean;
  cognitiveSkillsVersion: TCognitiveSkillsVersion;
  companyHideReports: boolean;
  isDevMode: boolean;
  projectStatus: ProjectStatus;
}

interface IStageGeneralSectionRow {
  firstLabel: string;
  firstValue: string;
  secondLabel?: string;
  secondValue?: string;
}

const COGNITIVE_MODE_OPTIONS = {
  [CognitiveMode.InDepth]: 'In-depth',
  [CognitiveMode.Streamlined]: 'Streamlined',
};

const StageOverview = ({
  stage,
  projectSubId,
  projectId,
  hasCompanyExpiryOption,
  cognitiveSkillsVersion,
  companyHideReports,
  isDevMode,
  projectStatus,
}: IStageOverview) => {
  const isAiInterview = stage.kind === AssessmentStageKind.AiInterview;
  return (
    <div className={styles.container}>
      <HeaderSection stage={stage} isDevMode={isDevMode} />
      <GeneralSection
        stage={stage}
        projectId={projectId}
        projectSubId={projectSubId}
        hasCompanyExpiryOption={hasCompanyExpiryOption}
        cognitiveSkillsVersion={cognitiveSkillsVersion}
        projectStatus={projectStatus}
      />
      {!isAiInterview && (
        <>
          <ReportsSection
            stage={stage}
            companyHideReports={companyHideReports}
          />
          <ProjectTeamEmailsSection stage={stage} />
        </>
      )}
    </div>
  );
};

export default StageOverview;

const HeaderSection = ({
  stage,
  isDevMode,
}: Pick<IStageOverview, 'stage' | 'isDevMode'>) => {
  const headerRows = [
    { label: 'Stage ID', value: stage.id },
    ...(isDevMode
      ? [{ label: 'Kind', value: stageKindLabel(stage.kind) }]
      : []),
  ];
  return (
    <div className={clsx(styles.row, styles.firstRow)}>
      {headerRows.map(({ label, value }) => (
        <InfoItem key={label} label={label} value={value} />
      ))}
    </div>
  );
};

const GeneralSection = ({
  stage,
  projectId,
  projectSubId,
  hasCompanyExpiryOption,
  cognitiveSkillsVersion,
  projectStatus,
}: Pick<
  IStageOverview,
  | 'stage'
  | 'projectId'
  | 'projectSubId'
  | 'hasCompanyExpiryOption'
  | 'cognitiveSkillsVersion'
  | 'projectStatus'
>) => {
  const isAiInterview = stage.kind === AssessmentStageKind.AiInterview;
  const expireCandidatesValue = hasCompanyExpiryOption
    ? getExpiryOptionLabel(stage.assessmentExpiryOption || null)
    : null;

  const stageGeneralSectionRows: IStageGeneralSectionRow[] = [
    {
      firstLabel: 'Start at',
      firstValue: getStageDate(stage.startTime),
      secondLabel: 'End at',
      secondValue: stage.endTime ? getStageDate(stage.endTime) : '—',
    },
    ...(isAiInterview
      ? expireCandidatesValue
        ? [
            {
              firstLabel: 'Expire candidates',
              firstValue: expireCandidatesValue,
            },
          ]
        : []
      : [
          {
            firstLabel: 'Cognitive assessment mode',
            firstValue: cognitiveSkillsVersion
              ? COGNITIVE_MODE_OPTIONS[stage.cognitiveMode]
              : emptyDisplayValue,
            ...(expireCandidatesValue && {
              secondLabel: 'Expire candidates',
              secondValue: expireCandidatesValue,
            }),
          },
        ]),
  ];

  return (
    <div className={styles.section}>
      <div className={styles.row}>
        <h3 className={styles.sectionTitle}>General</h3>
        <div className={styles.invitationActions}>
          <IndirectInvitationButton
            projectId={projectId}
            stageId={stage.id}
            disabled={projectStatus === ProjectStatus.Draft}
          />
          <CandidateListButton
            projectId={projectId}
            projectSubId={projectSubId}
            stageSubId={stage.id}
            disabledIndirectInvitationButton={
              projectStatus === ProjectStatus.Draft
            }
          />
        </div>
      </div>
      {stageGeneralSectionRows.map(
        ({ firstLabel, secondLabel, firstValue, secondValue }, index) => (
          <div key={`row-${index}`} className={styles.row}>
            <InfoItem label={firstLabel} value={firstValue} />
            {secondLabel && secondValue && (
              <InfoItem label={secondLabel} value={secondValue} />
            )}
          </div>
        ),
      )}
    </div>
  );
};

const ProjectTeamEmailsSection = ({ stage }: Pick<IStageOverview, 'stage'>) => {
  const stageMode = stage.emailSettings.projectTeamEmailSettings.mode;
  const projectTeamDetails = [
    {
      label: 'Mode',
      value: <ProjectModeTag mode={stageMode} />,
    },
    {
      label: 'Hiring role(s)',
      value: parseActorRolesToString(
        stage.emailSettings.projectTeamEmailSettings.actorRoles,
      ),
    },
  ];

  return (
    <div className={styles.section}>
      <div className={styles.row}>
        <h3 className={styles.sectionTitle}>Assessment completion emails</h3>
      </div>
      {projectTeamDetails.map(({ label, value }) => (
        <div
          key={label}
          className={clsx(styles.projectTeamRow, styles.longRow)}
        >
          <InfoItem label={label} value={value} />
        </div>
      ))}
    </div>
  );
};

const ReportsSection = ({
  stage,
  companyHideReports,
}: Pick<IStageOverview, 'stage' | 'companyHideReports'>) => {
  const stageReportRows = [
    {
      label: 'Hiring team report',
      value: stage.emailSettings.managerReport?.name || emptyDisplayValue,
    },
    {
      label: 'Candidate report',
      value: stage.emailSettings.candidateReport?.name || emptyDisplayValue,
    },
    {
      label: 'Email candidate the report',
      value: stage.emailSettings.candidateReport?.name
        ? getYesOrNo(stage.emailSettings.emailCandidateReport)
        : 'N/A',
    },
  ];

  const showReportsHiddenWarning =
    companyHideReports &&
    (stage.emailSettings.managerReport?.name ||
      stage.emailSettings.candidateReport?.name);

  return (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>Reports</h3>
      {showReportsHiddenWarning && (
        <InlineNotification notificationType={NotificationType.WARNING}>
          This role is set to generate reports, but the company setting prevents
          users from accessing report settings and reports via the company app.
        </InlineNotification>
      )}
      {stageReportRows.map(({ label, value }) => (
        <div key={label} className={styles.longRow}>
          <InfoItem label={label} value={value} />
        </div>
      ))}
    </div>
  );
};
